<template>
  <div class="content">
    <div class="row">
      <div class="col-12">
        <card>
          <template slot="header">
            <h4 class="card-title">Flujo de tickets</h4>
            <p class="card-category">
              Estados y transiciones del workflow seleccionado. Lectura del backend
              (<code>GET /api/Workflow/GetDiagram?workflowId=…</code>).
            </p>
            <!-- Selector de workflow (Fase 4 RFC dual-workflow). Persiste en query
                 string ?workflow=CODE para que el link sea compartible. -->
            <div class="form-inline mt-2" v-if="workflows.length > 1">
              <label class="mr-2 mb-0">Ver flujo:</label>
              <select
                class="form-control form-control-sm"
                v-model.number="selectedWorkflowId"
                @change="onWorkflowChange"
                data-testid="wf-diagram-selector"
              >
                <option v-for="w in workflows" :key="w.id" :value="w.id">
                  {{ w.name }}{{ w.isDefault ? ' (default)' : '' }}
                </option>
              </select>
            </div>
          </template>

          <div v-if="loading" class="text-center text-muted py-4">
            Cargando flujo…
          </div>

          <div v-else-if="error" class="alert alert-danger">
            No se pudo cargar el flujo: {{ error }}
          </div>

          <template v-else>
            <div class="workflow-diagram mb-4" v-html="diagramSvg"></div>

            <h5 class="mt-4">Detalle de transiciones</h5>
            <div class="table-responsive">
              <table class="table table-sm table-striped">
                <thead>
                  <tr>
                    <th>Acción</th>
                    <th>Desde</th>
                    <th>Hacia</th>
                    <th>Roles</th>
                    <th>Reglas</th>
                    <th>Notifica</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(t, i) in diagram.transitions" :key="i">
                    <td><code>{{ t.action }}</code></td>
                    <td>
                      <span v-for="fid in t.from" :key="fid" class="badge badge-secondary mr-1">
                        {{ stateName(fid) }}
                      </span>
                    </td>
                    <td><span class="badge badge-info">{{ stateName(t.to) }}</span></td>
                    <td>
                      <span v-for="r in t.roles" :key="r" class="badge badge-primary mr-1">{{ r }}</span>
                    </td>
                    <td class="small">
                      <span v-if="t.requiresComment" class="badge badge-warning mr-1">comentario</span>
                      <span v-if="t.requiresDeveloper" class="badge badge-warning mr-1">developer</span>
                      <span v-if="t.creatorOnly" class="badge badge-danger mr-1">solo creador</span>
                      <span v-if="t.incrementsQa" class="badge badge-light mr-1">++QA</span>
                    </td>
                    <td>
                      <span v-for="n in t.notifies" :key="n" class="badge badge-success mr-1">{{ n }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </card>
      </div>
    </div>
  </div>
</template>

<script>
import API from "@/services/Api";
import mermaid from "mermaid";

mermaid.initialize({ startOnLoad: false, securityLevel: "loose", theme: "default" });

export default {
  name: "workflow-diagram",
  data() {
    return {
      loading: true,
      error: null,
      diagram: null,
      diagramSvg: "",
      workflows: [],          // lista poblada de GET /api/Workflow
      selectedWorkflowId: null,
    };
  },
  async mounted() {
    try {
      // Cargar lista de workflows. Si falla (endpoint viejo pre-Fase 3), caemos al
      // diagrama sin selector — retrocompat con backend antes de dual-workflow.
      try {
        const listRes = await API.get("Workflow");
        const listTr = listRes.data || {};
        if (listTr.resultCode === 1 && Array.isArray(listTr.result)) {
          this.workflows = listTr.result;
        }
      } catch (_) { /* ignore — fallback: diagrama del default */ }

      // Determinar workflow inicial: query ?workflow=CODE tiene precedencia; si no,
      // el IsDefault=true; si no, el primero.
      const q = (this.$route && this.$route.query) ? this.$route.query.workflow : null;
      let initial = null;
      if (q && this.workflows.length) {
        initial = this.workflows.find((w) => w.code === String(q).toUpperCase());
      }
      if (!initial) initial = this.workflows.find((w) => w.isDefault) || this.workflows[0] || null;
      this.selectedWorkflowId = initial ? initial.id : null;

      await this.loadDiagram();
    } catch (e) {
      this.error = e.response && e.response.status === 401
        ? "No autorizado — vuelve a iniciar sesión."
        : (e.message || String(e));
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async loadDiagram() {
      const url = this.selectedWorkflowId
        ? `Workflow/GetDiagram?workflowId=${this.selectedWorkflowId}`
        : "Workflow/GetDiagram";
      const res = await API.get(url);
      const tr = res.data || {};
      if (tr.resultCode !== 1 || !tr.result) {
        this.error = (tr.resultMessages || ["Respuesta inesperada del servidor"]).join("; ");
        return;
      }
      this.diagram = tr.result;
      await this.$nextTick();
      await this.renderDiagram();
    },
    async onWorkflowChange() {
      // Persistir el workflow seleccionado en la URL (compartible + back button OK).
      const w = this.workflows.find((x) => x.id === this.selectedWorkflowId);
      if (w) {
        this.$router.replace({ query: { ...this.$route.query, workflow: w.code } })
          .catch(() => {}); // ignora NavigationDuplicated si es la misma URL
      }
      this.loading = true;
      this.error = null;
      try {
        await this.loadDiagram();
      } catch (e) {
        this.error = e.message || String(e);
      } finally {
        this.loading = false;
      }
    },
    stateName(id) {
      const s = this.diagram && this.diagram.states.find((x) => x.id === id);
      return s ? s.name : String(id);
    },
    buildMermaidSource() {
      const lines = ["stateDiagram-v2", "  direction LR"];
      this.diagram.states.forEach((s) => {
        // Sanitizamos para Mermaid: nada de comillas dentro del label.
        const safe = s.name.replace(/"/g, "'");
        lines.push(`  state "${safe}" as s${s.id}`);
      });
      this.diagram.transitions.forEach((t) => {
        const label = `${t.action} [${t.roles.join("/")}]`;
        t.from.forEach((from) => {
          lines.push(`  s${from} --> s${t.to}: ${label}`);
        });
      });
      return lines.join("\n");
    },
    async renderDiagram() {
      try {
        const src = this.buildMermaidSource();
        const id = "workflow-mermaid-" + Date.now();
        // Compatibilidad de firma entre versiones de Mermaid:
        // - v8 (CJS): mermaid.render(id, txt) devuelve el SVG string directamente (síncrono).
        // - v9/10/11 (ESM): mermaid.render(id, txt) devuelve una Promise<{svg}>.
        // Este repo usa v8 porque webpack 4 no transpila `??` desde .mjs (ver vue.config.js).
        const raw = mermaid.render(id, src);
        let svg;
        if (typeof raw === "string") {
          svg = raw;
        } else if (raw && typeof raw.then === "function") {
          const resolved = await raw;
          svg = resolved && typeof resolved === "object" ? resolved.svg : resolved;
        } else if (raw && raw.svg) {
          svg = raw.svg;
        } else {
          throw new Error("mermaid.render devolvió un resultado inesperado: " + typeof raw);
        }
        this.diagramSvg = svg;
      } catch (e) {
        // Si Mermaid revienta dejamos la tabla como fallback y mostramos el motivo.
        this.error = "Error renderizando diagrama: " + (e.message || String(e));
      }
    },
  },
};
</script>

<style scoped>
.workflow-diagram {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 1rem;
  overflow-x: auto;
}
.workflow-diagram >>> svg {
  max-width: 100%;
  height: auto;
}
</style>
